import * as React from 'react'
import { styled, css } from 'next-yak'
import * as Redux from 'react-redux'

import * as env from '@owl-nest/config'
import * as plume from '@ulule/owl-kit-components/next'

import { t, tc, tnc } from '@owl-nest/localize'

import { PreviewBar } from '@owl-nest/layout'
import { useCopy } from '@owl-nest/copier'

import * as projectModel from '../../models/project'
import { ComingSoonHeader } from './components/ComingSoonHeader'
import { Context } from './Context'
import { Home } from './Home'
import { PublicLayout } from '../../layout/PublicLayout'
import { RootState } from '../../../reducers'
import { useDisplayMode } from '../../hooks/useDisplayMode'
import { Description } from './Description'
import { SocialNetworks } from './SocialNetworks'

export function ComingSoon(): React.ReactElement<unknown> {
  const project = Redux.useSelector<RootState, projectModel.Project>((state) => state.application.project.data)

  const displayMode = useDisplayMode(project)
  const { canCopy, copy, copyStatus } = useCopy()
  const canonicalComingSoon = `${project.absolute_url}coming-soon`

  const [isTooltipOpen, setIsTooltipOpen] = React.useState(false)

  const $tooltipRef = React.useRef<HTMLDivElement>(null)
  plume.hooks.useOnClickOutside([$tooltipRef], () => setIsTooltipOpen(false))

  return (
    <PublicLayout>
      {displayMode === 'preview' && (
        <Preview>
          <PreviewBar
            translations={{
              title: t('Pre-launch page'),
              share: t('Share your pre-launch page'),
            }}
            previousEditionPath={`/projects/${project.id}/backoffice/prelaunch/`}
            onShare={() => {
              setIsTooltipOpen(!isTooltipOpen)
            }}
          />
          <CustomTooltip ref={$tooltipRef} open={isTooltipOpen} position="bottom" fitToContent>
            <plume.illustrations.twoToned.Share size={89} />
            <plume.styles.heading.S>{t('Share your pre-launch page')}</plume.styles.heading.S>
            <plume.styles.copy.M.light>
              {t(
                'Prepare for your launch by sharing your pre-launch page with your first circle and your community. Every subscriber will receive an email alert on the day of the launch!',
              )}
            </plume.styles.copy.M.light>

            <plume.TextField disabled format="wide" value={canonicalComingSoon} />

            <plume.styles.tooltip.BlockAnchor>
              <plume.Button
                disabled={!canCopy || !copyStatus}
                onClick={() => copy(canonicalComingSoon)}
                type="button"
                kind="secondary"
              >
                {t('Copy link')}
              </plume.Button>

              {canCopy && copyStatus && (
                <plume.Tooltip fitToContent position="top" open={copyStatus !== 'idle'}>
                  {copyStatus === 'succeeded' && (
                    <>
                      <plume.glyphs.stroke.CheckCircle size={16} />
                      <plume.styles.copy.S.regular>{t('Link copied to your clipboard.')}</plume.styles.copy.S.regular>
                    </>
                  )}
                  {copyStatus === 'failed' && (
                    <>
                      <plume.glyphs.stroke.TimesCircle size={16} />
                      <plume.styles.copy.S.regular>
                        {t("Sorry, we couldn't copy the link. Please try again!")}
                      </plume.styles.copy.S.regular>
                    </>
                  )}
                </plume.Tooltip>
              )}
            </plume.styles.tooltip.BlockAnchor>

            <ProjectFansCount>
              {project.fans_count === 0
                ? t("You don't have any fan yet, share your link!")
                : tnc('You currently have [count] fan', 'You currently have [count] fans', project.fans_count, {
                    count: (
                      <plume.styles.copy.L.semiBold
                        as="span"
                        dangerouslySetInnerHTML={{ __html: String(project.fans_count) }}
                      />
                    ),
                  })}
            </ProjectFansCount>
          </CustomTooltip>
        </Preview>
      )}
      <ComingSoonWrapper>
        <ComingSoonHeader />
        <ComingSoonContent />

        <Description />
        {project?.coming_soon?.show_social_networks && project.links && project.links.length > 0 && <SocialNetworks />}
      </ComingSoonWrapper>
      <LegalTextWrapper>
        <plume.styles.copy.XS.regular>
          {tc(
            'By subscribing to this fundraising project, you will first receive an email notification as soon as it is launched, followed by publications sent out by the project team.[br]You can unsubscribe from these communications at any time from the fundraising page.[br]Your e-mail address is also communicated to the project team, which agrees to use it only to communicate with you about the fundraising campaign. For more information, please consult [link: our privacy policy].',
            {
              br: <br />,
              link: <plume.Link href={env.PRIVACY_URL} target="_blank" rel="noopener noreferrer" />,
              strong: <strong />,
            },
          )}
        </plume.styles.copy.XS.regular>
      </LegalTextWrapper>
    </PublicLayout>
  )
}

export function ComingSoonContent(): React.ReactElement {
  return (
    <MainContent>
      <Context />
      <Home />
    </MainContent>
  )
}

const Preview = styled.div`
  position: relative;
`

const MainContent = styled.div`
  align-items: stretch;
  background-color: ${plume.COLORS.PRIMARY_SAND_100};
  border-radius: 4px;
  display: flex;
  flex-direction: column;
  margin: 0px 20px;

  @media screen and ${plume.BREAKPOINTS.TABLET} {
    margin: 0px 64px;
  }

  @media screen and ${plume.BREAKPOINTS.LAPTOP} {
    align-items: center;
  }
`

const ComingSoonWrapper = styled.div`
  ${MainContent} {
    margin-bottom: 10px;
  }

  ${plume.styles.copy.XS.regular} {
    text-align: center;
  }
`

const LegalTextWrapper = styled.div`
  margin: 25px auto;
  max-width: 80%;
  width: fit-content;

  ${plume.styles.copy.XS.regular} {
    color: ${plume.COLORS.TEXT_SECONDARY};
    text-align: center;
  }

  @media screen and ${plume.BREAKPOINTS.LAPTOP} {
    max-width: 850px;
    min-width: 850px;
    margin: 25px auto;
  }
`

const CustomTooltip = styled(plume.Tooltip)`
  align-items: flex-start;
  flex-direction: column;
  max-width: 100%;

  &:after {
    display: none;
  }

  ${plume.illustrations.twoToned.Share} {
    margin-bottom: 8px;
  }

  ${plume.styles.heading.S} {
    margin-bottom: 8px;
  }

  ${plume.styles.copy.M.light} {
    margin-bottom: 8px;
  }

  ${plume.TextField} {
    margin-bottom: 11px;
    width: 100%;
  }

  ${plume.styles.tooltip.BlockAnchor} {
    ${plume.glyphs.stroke.CheckCircle} {
      color: ${plume.COLORS.SECONDARY_GREEN_500};
      margin-right: 6px;
      margin-top: 2px;
    }

    ${plume.glyphs.stroke.TimesCircle} {
      color: ${plume.COLORS.PRIMARY_RED_500};
      margin-right: 6px;
      margin-top: 2px;
    }

    ${plume.Tooltip} {
      margin-left: 5px;

      ${plume.styles.copy.S.regular} {
        margin-bottom: 0;
      }
    }
  }

  @media screen and ${plume.BREAKPOINTS.TABLET} {
    top: 39px;
    right: -220px;
    padding: 32px;
    max-width: 476px;
    left: auto;
  }
`

const ProjectFansCount = styled(plume.styles.copy.S.regular)`
  margin-top: 16px;
`
